import Phaser from 'phaser';
import { makePlaceholderTextures } from '../placeholders';
import { setDebugEnabled } from '../debugMode';
import { MENU_TRACK, MUSIC_URLS } from '../audio/musicFiles';

// Collect the real atlas PNGs bundled into the build (key = filename), which
// override the same-key placeholders.
//
// The fully opaque ones — the 1280×720 backgrounds, the menu's two portrait
// cards, the leaderboard thumbnail and the Origin Event's two cards — ship as JPEG: the `?jpeg` query hands
// them to tools/vite-opaque-jpeg.ts at build time (~1 MB PNG → ~95 KB each; the
// build fails if one ever gains transparency). Keep the two globs' patterns in
// step, and the exclusions in devPopup.ts / achievementToast.ts too.
const OPAQUE_IMAGES = import.meta.glob(
  [
    '/assets/atlas/bg-*.png',
    '/assets/atlas/splash-hambi.png',
    '/assets/atlas/splash-nugget.png',
    '/assets/atlas/thumb-*.png',
    // The Origin Event's two portrait cards (2026-10-06).
    '/assets/atlas/origin-*.png',
  ],
  { eager: true, query: '?jpeg', import: 'default' },
) as Record<string, string>;

const ATLAS_IMAGES = import.meta.glob(
  [
    '/assets/atlas/*.png',
    '!/assets/atlas/bg-*.png',
    '!/assets/atlas/splash-hambi.png',
    '!/assets/atlas/splash-nugget.png',
    '!/assets/atlas/thumb-*.png',
    '!/assets/atlas/origin-*.png',
  ],
  { eager: true, query: '?url', import: 'default' },
) as Record<string, string>;

// Bundled SFX (same pattern as ATLAS_IMAGES). Empty until `npm run sfx` runs.
// Generated SFX are mp3; hand-supplied ones may ship as m4a (AAC) — both load
// under the same filename-stem key.
const AUDIO_FILES = import.meta.glob('/assets/audio/*.{mp3,m4a}', {
  eager: true,
  query: '?url',
  import: 'default',
}) as Record<string, string>;

// Bundled title-screen intro stings (key = filename, e.g. `intro1`). The menu
// picks one at random each time it opens. Same build-time glob pattern.
const INTRO_FILES = import.meta.glob('/assets/intros/*.mp3', {
  eager: true,
  query: '?url',
  import: 'default',
}) as Record<string, string>;

/** Registry key under which BootScene records the loaded intro-sting keys. */
export const INTRO_KEYS_REGISTRY = 'introKeys';

/**
 * BootScene
 * First scene to run. Loads any real atlas art (if present), then generates
 * placeholder textures for every key that was NOT already loaded as real art.
 * All other scenes consume textures by key and pick up real art automatically.
 */
export class BootScene extends Phaser.Scene {
  constructor() {
    super({ key: 'BootScene' });
  }

  preload(): void {
    // Queue real atlas art for loading (key = filename without .png extension).
    for (const [path, url] of [...Object.entries(ATLAS_IMAGES), ...Object.entries(OPAQUE_IMAGES)]) {
      const key = path
        .split('/')
        .pop()!
        .replace(/\.png$/, '');
      this.load.image(key, url);
    }

    // Queue real SFX (key = filename without extension). No-op when none exist yet.
    for (const [path, url] of Object.entries(AUDIO_FILES)) {
      const key = path
        .split('/')
        .pop()!
        .replace(/\.(mp3|m4a)$/, '');
      this.load.audio(key, url);
    }

    // Only the menu theme loads up front. The level tracks (3–6 MB each) are
    // fetched by MusicService the first time a level asks for one.
    if (MUSIC_URLS[MENU_TRACK]) this.load.audio(MENU_TRACK, MUSIC_URLS[MENU_TRACK]);

    // Queue intro stings and record their keys so MenuScene can pick one at
    // random. Empty (and the menu stays silent) until any intro mp3s exist.
    const introKeys: string[] = [];
    for (const [path, url] of Object.entries(INTRO_FILES)) {
      const key = path
        .split('/')
        .pop()!
        .replace(/\.mp3$/, '');
      this.load.audio(key, url);
      introKeys.push(key);
    }
    this.registry.set(INTRO_KEYS_REGISTRY, introKeys);
  }

  create(): void {
    // makePlaceholderTextures skips any key whose texture already exists
    // (i.e. was just loaded as real art above), so real art always wins.
    makePlaceholderTextures(this);

    // Secret debug/review entry: append ?debug to the URL to open DemoScene
    // (asset gallery + screen navigator) instead of the normal menu. Invisible
    // during normal play; ships in the build for on-device reviewing.
    if (new URLSearchParams(window.location.search).has('debug')) {
      setDebugEnabled(true);
      this.scene.start('DemoScene');
      return;
    }
    // The Hub's corner timers live in their own always-on-top scene.
    this.scene.launch('EffectTimerScene');
    this.scene.start('MenuScene');
  }
}
